import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
  Flag as FlagIcon,
  ReportProblem as ReportProblemIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import { TimestampDisplay } from "../internal/components/TimestampDisplay";
import { useNavigate } from "react-router-dom";
import { TimelineSidePanel } from "./shipments/TimelineSidePanel";
import { formatTimeIntervalReadable, formatDateMonthDay } from "../utils/time";
import { differenceInSeconds } from "date-fns";
import { useDispatch } from "react-redux";
import {
  openDatabaseObjectModal,
  openEmailModal,
} from "../store/adminSlice";

interface ShipmentReminderRenderInfo {
  label: string;
  value: string | Date;
  shipmentEmailId?: string;
}

interface ReminderWithUser {
  id: string;
  type: string;
  description: string | null;
  assignedUserIds: string[];
  assignedUsers?: Array<{ id: string; email: string; name: string }>;
  orgId: string;
  dueTime: string;
  triggerTime: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedTime: string | null;
  shipmentFeedId: string | null;
  shipmentEmailId: string | null;
  status: "pending" | "triggered" | "resolved" | "cancelled";
  isOverdue: boolean;
  renderInfo?: ShipmentReminderRenderInfo[];
}

interface ListRemindersResponse {
  reminders: ReminderWithUser[];
  total: number;
  offset: number;
  limit: number;
}

interface OrganizationInfo {
  id: string;
  name: string;
}

interface ReminderDefinition {
  type: string;
  name: string;
  category: "exception" | "milestone";
  description: string;
}

interface ListReminderTypesResponse {
  types: ReminderDefinition[];
}

const STATUS_COLORS: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success"
> = {
  pending: "warning",
  triggered: "info",
  resolved: "success",
  cancelled: "default",
};

export function AdminRemindersPage() {
  const [reminders, setReminders] = useState<ReminderWithUser[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [reminderDefinitions, setReminderDefinitions] = useState<
    Map<string, ReminderDefinition>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReminder, setSelectedReminder] =
    useState<ReminderWithUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timelinePanelOpen, setTimelinePanelOpen] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(
    null
  );

  // Filters
  const [filterOrgId, setFilterOrgId] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    fetchOrganizations();
    fetchReminderDefinitions();
  }, []);

  useEffect(() => {
    fetchReminders();
  }, [page, rowsPerPage, filterOrgId, filterStatus, filterType]);

  const fetchOrganizations = async () => {
    try {
      const res = await apiClient.post<{ organizations: OrganizationInfo[] }>(
        `/admin/organizations/list`,
        {}
      );
      setOrganizations(res.organizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchReminderDefinitions = async () => {
    try {
      const res = await apiClient.post<ListReminderTypesResponse>(
        `/shipments/reminders/list-reminder-types`,
        { includeInternalReminders: true }
      );
      const defsMap = new Map(res.types.map((def) => [def.type, def]));
      setReminderDefinitions(defsMap);
      // Also set available types for the filter dropdown
      setAvailableTypes(res.types.map((def) => def.type).sort());
    } catch (error) {
      console.error("Error fetching reminder definitions:", error);
    }
  };

  const fetchReminders = async () => {
    try {
      setLoading(true);
      setError(null);

      const requestBody: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        includeInternalReminders: true, // Admin page shows all reminders including internal
      };

      if (filterOrgId) requestBody.orgId = filterOrgId;
      if (filterStatus) requestBody.status = filterStatus;
      if (filterType) requestBody.type = filterType;

      const res = await apiClient.post<ListRemindersResponse>(
        `/shipments/reminders/list-reminders`,
        requestBody
      );

      setReminders(res.reminders);
      setTotal(res.total);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      setError("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    reminder: ReminderWithUser
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedReminder(reminder);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReminder(null);
  };

  const handleResolveReminder = async () => {
    if (!selectedReminder) return;

    try {
      await apiClient.post(`/shipments/reminders/resolve-reminder`, {
        reminderId: selectedReminder.id,
        reason: "Manually resolved by admin",
      });

      // Refresh reminders list
      fetchReminders();
      handleMenuClose();
    } catch (error) {
      console.error("Error resolving reminder:", error);
      alert("Failed to resolve reminder");
    }
  };

  const handleUnresolveReminder = async () => {
    if (!selectedReminder) return;

    try {
      await apiClient.post(`/shipments/reminders/update-reminder`, {
        reminderId: selectedReminder.id,
        resolve: false,
        reason: "Manually unresolved by admin",
      });

      // Refresh reminders list
      fetchReminders();
      handleMenuClose();
    } catch (error) {
      console.error("Error unresolving reminder:", error);
      alert("Failed to unresolve reminder");
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedReminder
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReminder) return;

    try {
      await apiClient.post(`/shipments/reminders/delete-reminder`, {
        reminderId: selectedReminder.id,
      });

      // Refresh reminders list
      fetchReminders();
      setDeleteDialogOpen(false);
      setSelectedReminder(null);
    } catch (error) {
      console.error("Error deleting reminder:", error);
      alert("Failed to delete reminder");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedReminder(null); // Clear selection when canceling
  };

  const handleViewShipmentTimeline = () => {
    if (!selectedReminder?.shipmentFeedId) return;

    // Open timeline in side panel
    setSelectedShipmentId(selectedReminder.shipmentFeedId);
    setTimelinePanelOpen(true);
    handleMenuClose();
  };

  const handleShipmentIdClick = (shipmentId: string) => {
    setSelectedShipmentId(shipmentId);
    setTimelinePanelOpen(true);
  };

  const handleTimelinePanelClose = () => {
    setTimelinePanelOpen(false);
    setSelectedShipmentId(null);
  };

  const handleReminderIdClick = (reminderId: string) => {
    dispatch(openDatabaseObjectModal({ table: "reminder", id: reminderId }));
  };

  const handleViewActionLogs = () => {
    if (!selectedReminder) return;

    // Navigate to action log page filtered by this reminder
    navigate(`/admin/action-logs?reminderId=${selectedReminder.id}`);
    handleMenuClose();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilterOrgId("");
    setFilterStatus("");
    setFilterType("");
    setPage(0);
  };

  return (
    <AdminPage title="Shipment Reminders">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={filterOrgId}
              label="Organization"
              onChange={(e) => {
                setFilterOrgId(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="triggered">Triggered</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              {availableTypes.map((type) => {
                const def = reminderDefinitions.get(type);
                return (
                  <MenuItem key={type} value={type}>
                    {def?.name ? `${def.name} (${type})` : type}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={handleResetFilters}>
            Reset Filters
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Showing {reminders.length} of {total} reminders
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reminder ID</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Triggered</TableCell>
                  <TableCell>Resolved In</TableCell>
                  <TableCell>Shipment ID</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reminders.map((reminder) => {
                  const definition = reminderDefinitions.get(reminder.type);
                  const category = definition?.category;

                  return (
                    <TableRow key={reminder.id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            cursor: "pointer",
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                            wordBreak: "break-all",
                          }}
                          onClick={() => handleReminderIdClick(reminder.id)}
                        >
                          {reminder.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {category === "milestone" && (
                            <FlagIcon fontSize="small" color="primary" />
                          )}
                          {category === "exception" && (
                            <ReportProblemIcon fontSize="small" color="warning" />
                          )}
                          <Typography variant="body2">
                            {category || "-"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                        >
                          {reminder.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {definition?.name
                            ? `${definition.name} (${reminder.type})`
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={reminder.status.toUpperCase()}
                            size="small"
                            color={STATUS_COLORS[reminder.status] || "default"}
                          />
                          {reminder.isOverdue && (
                            <Chip
                              label="OVERDUE"
                              size="small"
                              color="error"
                              variant="filled"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {reminder.assignedUsers?.map((user) => (
                            <Typography key={user.id} variant="body2">
                              {user.email}
                            </Typography>
                          )) || (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {reminder.triggerTime ? (
                          <TimestampDisplay timestamp={reminder.triggerTime} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {reminder.resolvedTime && reminder.triggerTime ? (
                          <Typography variant="body2">
                            {formatTimeIntervalReadable(
                              differenceInSeconds(
                                new Date(reminder.resolvedTime),
                                new Date(reminder.triggerTime)
                              )
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {reminder.shipmentFeedId ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              cursor: "pointer",
                              color: "primary.main",
                              "&:hover": { textDecoration: "underline" },
                              wordBreak: "break-all",
                            }}
                            onClick={() =>
                              handleShipmentIdClick(reminder.shipmentFeedId!)
                            }
                          >
                            {reminder.shipmentFeedId}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {reminder.description || "-"}
                          </Typography>
                          {reminder.renderInfo &&
                            reminder.renderInfo.length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                {reminder.renderInfo.map((info, index) => {
                                  const formattedValue =
                                    info.value instanceof Date
                                      ? formatDateMonthDay(info.value)
                                      : typeof info.value === "string" &&
                                        !isNaN(Date.parse(info.value))
                                      ? formatDateMonthDay(new Date(info.value))
                                      : info.value;

                                  return (
                                    <Box
                                      key={index}
                                      sx={{
                                        fontSize: "0.75rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.25,
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {info.label}:
                                      </Typography>
                                      {info.shipmentEmailId ? (
                                        <Box
                                          component="span"
                                          onClick={() =>
                                            dispatch(
                                              openEmailModal(
                                                info.shipmentEmailId!
                                              )
                                            )
                                          }
                                          sx={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            color: "primary.main",
                                            fontSize: "0.75rem",
                                            "&:hover": {
                                              color: "primary.dark",
                                            },
                                          }}
                                        >
                                          <EmailIcon
                                            sx={{ fontSize: "0.875rem" }}
                                          />
                                          {formattedValue}
                                        </Box>
                                      ) : (
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {formattedValue}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, reminder)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedReminder?.status === "pending" && (
          <MenuItem onClick={handleResolveReminder}>Resolve Reminder</MenuItem>
        )}
        {selectedReminder?.status === "resolved" && (
          <MenuItem onClick={handleUnresolveReminder}>Unresolve Reminder</MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>Delete Reminder</MenuItem>
        {selectedReminder?.shipmentFeedId && (
          <MenuItem onClick={handleViewShipmentTimeline}>
            <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
            Open Shipment Timeline
          </MenuItem>
        )}
        <MenuItem onClick={handleViewActionLogs}>
          <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
          View Action Logs
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Reminder</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this reminder? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shipment Timeline Side Panel */}
      <TimelineSidePanel
        open={timelinePanelOpen}
        onClose={handleTimelinePanelClose}
        shipmentId={selectedShipmentId || undefined}
        includeInternalReminders={true}
      />
    </AdminPage>
  );
}
